import { useState, type ReactNode } from "react";
import { Box, Drawer, Group, LoadingOverlay, Text } from "@mantine/core";
import { Alert, Button, ConfirmModal } from "@xplatform/design-system";
import { IconDeviceFloppy } from "@tabler/icons-react";
import { useTranslation } from "@hooks/useTranslation";
import "./EditorDrawer.scss";

interface EditorDrawerProps {
    opened: boolean;
    onClose: () => void;
    title: ReactNode;
    /** Unsaved edits: closing asks before discarding them. */
    dirty: boolean;
    saving: boolean;
    error?: string | null;
    onDismissError?: () => void;
    saveLabel: string;
    /** Why saving is blocked (e.g. an invalid schema); shown beside the button. */
    blockedReason?: string | null;
    onSave: () => void;
    /** Read-only viewers get no save button. */
    canEdit?: boolean;
    children: ReactNode;
}

/**
 * The side panel the flow page edits a step or an agent in. The page stays
 * visible behind it, its footer is always on screen, a failed save is reported
 * inside the panel (not in a toast nobody renders), and unsaved edits are never
 * thrown away without asking.
 */
export const EditorDrawer = ({
    opened,
    onClose,
    title,
    dirty,
    saving,
    error,
    onDismissError,
    saveLabel,
    blockedReason,
    onSave,
    canEdit = true,
    children,
}: EditorDrawerProps) => {
    const { t } = useTranslation();
    const [confirmDiscard, setConfirmDiscard] = useState(false);

    const requestClose = () => {
        if (saving) return;
        if (dirty) setConfirmDiscard(true);
        else onClose();
    };

    return (
        <>
            <Drawer
                opened={opened}
                onClose={requestClose}
                position="right"
                size="min(1200px, 96vw)"
                title={title}
                className="editor-drawer"
                portalProps={{ target: "#admin-portal-root" }}
                closeButtonProps={{ disabled: saving, "aria-label": t("admin.flowConfigs.editor.close") }}
                styles={{
                    content: { display: "flex", flexDirection: "column" },
                    body: { display: "flex", flexDirection: "column", flex: 1, minHeight: 0, paddingBottom: 0 },
                }}
            >
                <LoadingOverlay visible={saving} />
                {error && (
                    <Alert color="red" mb="md" withCloseButton={!!onDismissError} onClose={onDismissError}>
                        {error}
                    </Alert>
                )}
                <Box className="editor-drawer__content">{children}</Box>
                <Group justify="space-between" className="editor-drawer__footer" wrap="nowrap">
                    <Text size="xs" c={dirty ? "orange.7" : "dimmed"}>
                        {blockedReason ?? (dirty ? t("admin.flowConfigs.editor.unsaved") : t("admin.flowConfigs.editor.noChanges"))}
                    </Text>
                    <Group gap="sm" wrap="nowrap">
                        <Button variant="subtle" onClick={requestClose} disabled={saving}>
                            {canEdit ? t("common.cancel") : t("admin.flowConfigs.editor.close")}
                        </Button>
                        {canEdit && (
                            <Button
                                onClick={onSave}
                                leftSection={<IconDeviceFloppy size={16} />}
                                disabled={saving || !dirty || !!blockedReason}
                            >
                                {saveLabel}
                            </Button>
                        )}
                    </Group>
                </Group>
            </Drawer>

            <ConfirmModal
                opened={confirmDiscard}
                onClose={() => setConfirmDiscard(false)}
                onConfirm={() => {
                    setConfirmDiscard(false);
                    onClose();
                }}
                title={t("admin.flowConfigs.editor.discardTitle")}
                message={t("admin.flowConfigs.editor.discardMessage")}
                confirmLabel={t("admin.flowConfigs.editor.discard")}
                cancelLabel={t("admin.flowConfigs.editor.keepEditing")}
                danger
                portalProps={{ target: "#admin-portal-root" }}
            />
        </>
    );
};
